package org.vreo.devicehub.features.device.domain

import androidx.compose.ui.graphics.ImageBitmap

import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.EmptyResult
import org.vreo.devicehub.features.core.domain.Result
import org.vreo.devicehub.features.core.domain.device.videoCameraConfig.VideoConfig
import org.vreo.devicehub.features.core.domain.device.device.AnprDevice
import org.vreo.devicehub.features.core.domain.device.message.AnprCameraMessage

/**
 * Device access repository, allows to access remote devices accessable to user's account
 */
interface DeviceAccessRepository {
    /**
     * Get user devices, all the devices linked to user's account
     *
     * @param token user authentication token
     * @return Result of list of devices on success or error on failure
     */
    suspend fun getUserDevices(
        token: String
    ): Result<List<AnprDevice>, DataError.Remote>

    /**
     * Add device to authenticated account using authentication token [token] and provided device
     * data
     *
     * @param token user authentication token
     * @param productNumber product number of device
     * @param serialNumber device serial number
     * @param tag device tag, optional device identifier
     * @param linkingKey linking key/password
     * @return Empty result on success or error on failure
     */
    suspend fun addDeviceToAccount(
        token: String,
        productNumber: String,
        serialNumber: Long,
        tag: String?,
        linkingKey: String
    ): EmptyResult<DataError.Remote>

    /**
     * Unlink device from account
     *
     * @param token user authentication token
     * @param deviceId device ID to be unlinked
     * @return Empty result on success or error on failure
     */
    suspend fun unlinkDeviceFromAccount(
        token: String,
        deviceId: String
    ): EmptyResult<DataError.Remote>

    /**
     * Delete device from production environment, this method will remove target device from
     * registered service and device will no longer be able to be accessed or used
     *
     * @param token user authentication token
     * @param deviceId device ID to be deleted
     * @return Empty result on success or error on failure
     */
    suspend fun deleteDeviceFromProduction(
        token: String,
        deviceId: String
    ): EmptyResult<DataError.Remote>

    /**
     * Get device production register (xlsx file). File includes details of all devices
     * registered in production database
     *
     * @param token user authentication token
     * @return Result of device production register on success or error on failure
     */
    suspend fun getDeviceProductionRegister(
        token: String
    ): Result<ByteArray, DataError.Remote>

    /**
     * Register new production ready device with all required backend services
     *
     * @param token user authentication token
     * @param productNumber device production number, allows to register device as one of
     * supported device from allowed categories
     * @param serialNumber serial number to be used for target device
     * @return registered device details on success or error on failure
     */
    suspend fun registerNewDevice(
        token: String,
        productNumber: String,
        serialNumber: Long? = null,
    ): Result<AnprDevice, DataError.Remote>

    /**
     * Get messages for selected device ID
     *
     * @param token user authentication token
     * @param deviceId ID of the device to access
     * @param limit maximum number of messages to be retrieved
     * @param fromTime unix timestamp in seconds of from when to retrieve message
     * @param toTime unix timestamp in seconds of to when to retrieve messages
     * @return Result of list of messages on success or error on failure
     */
    suspend fun getMessages(
        token: String,
        deviceId: String,
        limit: Int = 20,
        fromTime: Long? = null,
        toTime: Long? = null,
        ignoreIds: List<String>? = null
    ): Result<List<AnprCameraMessage>, DataError.Remote>

    /**
     * Get image from remote data source
     *
     * @param token user authentication token
     * @param path path to image, this should be acquired from the [AnprCameraMessage]
     * @return Result of image on success or error on failure
     */
    suspend fun getImage(
        token: String,
        path: String,
    ): Result<ImageBitmap?, DataError.Remote>

    /**
     * Get device's video configuration
     *
     * @param token user authentication token
     * @param deviceId device ID
     * @return Result of video configuration on success or error on failure
     */
    suspend fun getVideoConfiguration(
        token: String,
        deviceId: String
    ): Result<VideoConfig, DataError.Remote>

    /**
     * Set device's video configuration
     *
     * @param token user authentication token
     * @param deviceId device ID
     * @param config video configuration to be sent to device
     * @return Result of empty on success or error on failure
     */
    suspend fun setVideoConfiguration(
        token: String,
        deviceId: String,
        config: VideoConfig
    ): EmptyResult<DataError.Remote>

    /**
     * Connect to device on local network
     *
     * @param deviceIpAddress device ip address
     * @param devicePort device port
     * @param password device password
     * @return Result of empty on success or error on failure
     */
    suspend fun connectToLocalDevice(
        deviceIpAddress: String,
        devicePort: String = "5050",
        password: String = "voltcam1!"
    ): EmptyResult<DataError.Remote>

    /**
     * Apply device configuration. Send configuration to device on local network.
     *
     * @param deviceIpAddress device ip address
     * @param device device configuration
     * @param devicePort device port
     * @param password device password
     * @return Result of empty on success or error on failure
     */
    suspend fun applyDeviceConfiguration(
        deviceIpAddress: String,
        device: AnprDevice,
        devicePort: String = "5050",
        password: String = "voltcam1!"
    ): EmptyResult<DataError.Remote>
}